/**
 * Task 26: User Upload Modal
 * Bulk user upload via CSV/Excel with validation and summary
 */

import { useState } from 'react';
import { X, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download } from 'lucide-react';
import axios from '../config/axiosConfig';

const UserUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (!validTypes.includes(fileExt)) {
        setError('Invalid file type. Please upload CSV or Excel file.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/upload/bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      
      if (response.data.successful > 0 && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError('');
    onClose();
  };

  const downloadTemplate = () => {
    // Create CSV template for candidate upload
    const template = `Name,Email,Phone,Role,Location,CallOutcome
John Doe,john@candidate.com,9876543210,recruiter,Pune,Not Contacted
Jane Smith,jane@candidate.com,9988776655,recruiter,Mumbai,Interested`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal to-gold text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <h2 className="text-2xl font-bold font-heading">Bulk User Upload</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Instructions
            </h3>
            <ul className="text-sm text-blue-800 space-y-1 ml-7 list-disc">
              <li>Upload a CSV or Excel file with user data</li>
              <li>Required columns: <strong>FullName, Email, Role</strong></li>
              <li>Optional columns: ManagerName, Mobile, Location</li>
              <li>Valid roles: admin, manager, bm, am, tl, recruiter</li>
              <li>Maximum 500 users per upload</li>
            </ul>
          </div>

          {/* Download Template */}
          <button
            onClick={downloadTemplate}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-teal text-teal rounded-lg hover:bg-teal hover:text-white transition font-semibold"
          >
            <Download className="w-5 h-5" />
            Download CSV Template
          </button>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-semibold text-textLight mb-2">
              Select File
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="w-full px-4 py-3 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal file:text-white file:cursor-pointer hover:border-teal transition"
              />
            </div>
            {file && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {result && (
            <div className="border-2 border-gray-200 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-lg text-textLight">Upload Results</h3>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{result.total_rows}</div>
                  <div className="text-sm text-blue-700">Total Rows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{result.successful}</div>
                  <div className="text-sm text-green-700">Created</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{result.failed}</div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
              </div>

              {/* Success List */}
              {result.created_users && result.created_users.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Successfully Created ({result.created_users.length})
                  </h4>
                  <div className="bg-green-50 rounded-lg p-3 max-h-40 overflow-auto">
                    <ul className="text-sm text-green-800 space-y-1">
                      {result.created_users.map((email, idx) => (
                        <li key={idx}>✓ {email}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Error List */}
              {result.errors && result.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Errors ({result.errors.length})
                  </h4>
                  <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-auto">
                    <ul className="text-sm text-red-800 space-y-2">
                      {result.errors.map((err, idx) => (
                        <li key={idx} className="border-b border-red-200 pb-2 last:border-0">
                          <strong>Row {err.row}:</strong> {err.errors.join(', ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Close
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-teal to-gold text-white rounded-lg hover:shadow-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload Users'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserUploadModal;
